import axios from 'axios';
import { ROUTES } from '../constants/routes';

// Instance without 401 error handling
export const httpWithout401 = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api',
    // baseURL: 'https://infintegration.devitdelight.com/api',
});

// Instance symfony
export const httpSymfony = axios.create({
    baseURL: process.env.REACT_APP_SYMFONY_API_URL || '/api', // Changed to more standard env name
    // DONT DELETE this for local dev
    // baseURL: 'https://infintegration.symfony.devitdelight.com/api',
});

httpSymfony.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            // Clean token and add to headers
            config.headers['Authorization'] = `Bearer ${token.replace(/['"]+/g, '')}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Optional: Add response interceptor if needed
httpSymfony.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            // Handle Symfony API 401 errors differently if needed
            console.error('Symfony API authentication error');
        }
        return Promise.reject(error);
    }
);

// Instance with 401 error handling
const http = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api',
    // baseURL: 'https://infintegration.devitdelight.com/api',
});
// Add a request interceptor to check token before each request
http.interceptors.request.use(
    (config) => {
        let token = localStorage.getItem('token');
        if (token) {
            // Encode token to ensure it uses valid characters
            token = encodeURIComponent(token);
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add a response interceptor to handle 401 errors
http.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('token');
            const loginUrl = `${window.location.origin}${process.env.PUBLIC_URL}${ROUTES.LOGIN}`;
            if (!window.location.href.startsWith(loginUrl)) {
                window.location.href = `${loginUrl}?massage_error=Session has expired. Please re-authorize.`;
            }
        }

        if (error.response && error.response.status === 429) {
            const retryAfter = Number(error.response.headers?.['retry-after']);
            error.response.data = {
                ...(error.response.data || {}),
                message: retryAfter > 0
                    ? `Too many requests. Please try again in ${retryAfter} second${retryAfter === 1 ? '' : 's'}.`
                    : 'Too many requests. Please try again in a minute.',
            };
        }

        return Promise.reject(error);
    }
);

// Client-side rate limiter mirroring the server per-endpoint throttle (60 req / 60s).
// When an endpoint reaches its limit the request WAITS for a free slot in the rolling
// window instead of being sent and rejected with 429.
const RATE_LIMIT = 60;
const RATE_WINDOW = 60000;
const rateHits = new Map();

// Collapse a URL to the server route-pattern bucket: drop the query string and replace
// id-like path segments (numeric / uuid / hash) with ':id', so /flows/12 and /flows/13
// share one bucket, exactly like Laravel's route()->uri().
const isIdSegment = (seg) => /^\d+$/.test(seg) || (/\d/.test(seg) && /^[0-9a-f-]{6,}$/i.test(seg));

const rateKey = (config) =>
    String(config?.url ?? '')
        .split('?')[0]
        .split('/')
        .map((seg) => (isIdSegment(seg) ? ':id' : seg))
        .join('/');

const acquireRateSlot = (key) =>
    new Promise((resolve) => {
        const now = Date.now();
        const hits = (rateHits.get(key) || []).filter((t) => now - t < RATE_WINDOW);
        rateHits.set(key, hits);

        if (hits.length < RATE_LIMIT) {
            hits.push(now);
            resolve();
            return;
        }

        const wait = hits[0] + RATE_WINDOW - now;
        // eslint-disable-next-line no-console
        console.warn(`[rate-limit] endpoint "${key}" saturated (${RATE_LIMIT}/${RATE_WINDOW / 1000}s), waiting ${Math.ceil(wait / 1000)}s`);
        setTimeout(() => acquireRateSlot(key).then(resolve), wait + 1);
    });

http.interceptors.request.use((config) => acquireRateSlot(rateKey(config)).then(() => config));

const inflightGets = new Map();
const originalGet = http.get.bind(http);

http.get = (url, config) => {
    const key = JSON.stringify([url, config?.params ?? null, config?.responseType ?? null]);
    const pending = inflightGets.get(key);

    if (pending) {
        return pending;
    }

    const request = originalGet(url, config).finally(() => inflightGets.delete(key));
    inflightGets.set(key, request);

    return request;
};

export default http;
